import { analyticsRepository } from "@/repositories/analytics";
import { useStore } from "@/stores/useStore";
import { useCallback } from "react";

export const CustomerUpdateUsageLocation = {
  TOAST: "toast",
  DIALOG: "dialog",
  PAGE: "page",
  PANEL: "panel",
} as const;

export type CustomerUpdateUsageLocation =
  (typeof CustomerUpdateUsageLocation)[keyof typeof CustomerUpdateUsageLocation];

const CustomerUpdateAnalyticsEventNames = {
  snoozeAllUsed: "customer_updates_snooze_all_used",
  snoozeUsed: "customer_updates_snooze_used",
  customerUpdateCompleted: "customer_update_completed",
  filtersUsed: "customer_updates_filters_used",
  quickTimeUsed: "customer_updates_quick_time_used",
  customTimeUsed: "customer_updates_custom_time_used",
  userChanged: "customer_updates_user_changed",
} as const;

type CustomerUpdateAnalyticsEventNames =
  (typeof CustomerUpdateAnalyticsEventNames)[keyof typeof CustomerUpdateAnalyticsEventNames];

export const useCustomerUpdatesAnalytics = () => {
  const shopId = useStore((s) => s.shopId);

  const analyticsWrapper = useCallback(
    (
      eventName: CustomerUpdateAnalyticsEventNames,
      params?: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
    ) => {
      if (!shopId) {
        console.error(
          "Shop ID is missing, cannot log analytics event:",
          eventName
        );
        return;
      }
      analyticsRepository.logAnalyticsEvent(eventName, {
        ...params,
        shopId,
      });
    },
    [shopId]
  );

  const logSnoozeAllUsed = () => {
    analyticsWrapper(CustomerUpdateAnalyticsEventNames.snoozeAllUsed);
  };

  const logSnoozeUsed = (location: CustomerUpdateUsageLocation) => {
    analyticsWrapper(CustomerUpdateAnalyticsEventNames.snoozeUsed, {
      location,
    });
  };

  const logCustomerUpdateCompleted = (
    location: CustomerUpdateUsageLocation,
    userNotDefault: boolean,
    timeUntilUsed: string | number
  ) => {
    analyticsWrapper(
      CustomerUpdateAnalyticsEventNames.customerUpdateCompleted,
      {
        location,
        userNotDefault,
        timeUntilUsed,
      }
    );
  };

  const logFiltersUsed = (filters: Record<string, boolean>) => {
    analyticsWrapper(CustomerUpdateAnalyticsEventNames.filtersUsed, {
      ...filters,
    });
  };

  const logQuickTimeUsed = (
    location: CustomerUpdateUsageLocation,
    timeOption: string | number | undefined
  ) => {
    analyticsWrapper(CustomerUpdateAnalyticsEventNames.quickTimeUsed, {
      timeOption,
      location,
    });
  };

  const logCustomTimeUsed = (location: CustomerUpdateUsageLocation) => {
    analyticsWrapper(CustomerUpdateAnalyticsEventNames.customTimeUsed, {
      location,
    });
  };

  const logUserChanged = (userId: string) => {
    analyticsWrapper(CustomerUpdateAnalyticsEventNames.userChanged, {
      userId,
    });
  };

  return {
    logSnoozeAllUsed,
    logSnoozeUsed,
    logCustomerUpdateCompleted,
    logQuickTimeUsed,
    logCustomTimeUsed,
    logFiltersUsed,
    logUserChanged,
  };
};
